if (!customElements.get('rs-announcement-animate')) {
  customElements.define('rs-announcement-animate', class extends HTMLElement {
    _index = 0;
    _timer = null;
    _isPaused = false;
    _isHovered = false;
    _isFocused = false;

    _handleControlClick = (event) => {
      const button = event.target.closest('button');
      if (!button) return;

      if (button.hasAttribute('data-rs-ann-toggle')) {
        this._togglePlayback();
      } else if (button.hasAttribute('data-rs-ann-prev')) {
        this._step(-1, true);
      } else if (button.hasAttribute('data-rs-ann-next')) {
        this._step(1, true);
      }
    };

    // Hover and focus hold the rotation; an explicit pause outranks both
    _handleMouseEnter = () => {
      this._isHovered = true;
      this._updateTimer();
    };

    _handleMouseLeave = () => {
      this._isHovered = false;
      this._updateTimer();
    };

    _handleFocusIn = () => {
      this._isFocused = true;
      this._updateTimer();
    };

    _handleFocusOut = (event) => {
      if (this._scope.contains(event.relatedTarget)) return;
      this._isFocused = false;
      this._updateTimer();
    };

    connectedCallback() {
      this._items = Array.from(this.querySelectorAll('.rs-ann-animate__item'));
      if (this._items.length === 0) return;

      this._speed = parseInt(this.dataset.speed) || 7000;
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._scope = this.closest('announcement-bar') || this;
      this._controls = this._scope.querySelector('[data-rs-ann-controls]');
      this._toggleButton = this._scope.querySelector('[data-rs-ann-toggle]');
      this._status = this._scope.querySelector('[data-rs-ann-status]');

      // Only the visible message belongs in the tab order and the a11y tree
      this._items.forEach((item, index) => this._setHidden(item, index !== 0));
      this._enter(0);

      // A single message never rotates, so it needs no controls
      if (this._items.length < 2) {
        this._controls?.remove();
        return;
      }

      this._controls?.addEventListener('click', this._handleControlClick);
      this.addEventListener('mouseenter', this._handleMouseEnter);
      this.addEventListener('mouseleave', this._handleMouseLeave);
      this._scope.addEventListener('focusin', this._handleFocusIn);
      this._scope.addEventListener('focusout', this._handleFocusOut);

      this._updateTimer();
    }

    disconnectedCallback() {
      this._stopTimer();
      this._controls?.removeEventListener('click', this._handleControlClick);
      this.removeEventListener('mouseenter', this._handleMouseEnter);
      this.removeEventListener('mouseleave', this._handleMouseLeave);
      this._scope?.removeEventListener('focusin', this._handleFocusIn);
      this._scope?.removeEventListener('focusout', this._handleFocusOut);
    }

    _updateTimer() {
      if (this._isPaused || this._isHovered || this._isFocused) {
        this._stopTimer();
      } else {
        this._startTimer();
      }
    }

    _startTimer() {
      this._stopTimer();
      this._timer = setInterval(() => this._step(1, false), this._speed);
    }

    _stopTimer() {
      clearInterval(this._timer);
      this._timer = null;
    }

    _togglePlayback() {
      this._isPaused = !this._isPaused;

      // Pressing play has to win over the hover/focus hold the click itself created
      if (!this._isPaused) {
        this._isHovered = false;
        this._isFocused = false;
      }

      const state = this._isPaused ? 'play' : 'pause';
      this._toggleButton.setAttribute('aria-label', this._toggleButton.dataset[`${state}Label`]);
      this._toggleButton.querySelector('[data-rs-ann-toggle-text]').textContent =
        this._toggleButton.dataset[`${state}Text`];

      this._updateTimer();
    }

    // opacity alone leaves a slide focusable and readable, so state has to be explicit
    _setHidden(item, isHidden) {
      item.inert = isHidden;

      if (isHidden) {
        item.setAttribute('aria-hidden', 'true');
      } else {
        item.removeAttribute('aria-hidden');
      }

      item.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((el) => {
        if (isHidden) {
          el.setAttribute('tabindex', '-1');
        } else {
          el.removeAttribute('tabindex');
        }
      });
    }

    _transitionDelay() {
      return this._motionQuery.matches ? 0 : 350;
    }

    _enter(index) {
      this._items[index].classList.remove('rs-ann--exiting');
      this._items[index].classList.add('rs-ann--entering');
    }

    _exit(index) {
      this._items[index].classList.remove('rs-ann--entering');
      this._items[index].classList.add('rs-ann--exiting');
    }

    _step(direction, isManual) {
      const outgoing = this._index;
      this._index = (this._index + direction + this._items.length) % this._items.length;

      this._exit(outgoing);
      this._setHidden(this._items[outgoing], true);

      // Wait for the exit animation before entering the next item
      setTimeout(() => {
        this._items[outgoing].classList.remove('rs-ann--exiting');
        this._enter(this._index);
        this._setHidden(this._items[this._index], false);

        if (isManual && this._status) {
          this._status.textContent = this._items[this._index].textContent.replace(/\s+/g, ' ').trim();
        }
      }, this._transitionDelay());
    }
  });
}
